import React, { useEffect, useState } from 'react';
import { connect } from 'react-redux';
import {
    Box, Button, TextField, Typography, MenuItem, Select,
    FormControl, InputLabel, Grid, Paper, CircularProgress, Alert,
    Tabs, Tab, InputAdornment, Tooltip, IconButton, Chip,
    FormHelperText,
    Link,
    Stack
} from '@mui/material';
import ContentCopyIcon from '@mui/icons-material/ContentCopy';
import OpenInNewIcon from '@mui/icons-material/OpenInNew';
import AutorenewIcon from '@mui/icons-material/Autorenew';
import { InfluencerStorefrontService } from '../../../../core/services/influencer-storefront.service';
import { BASE_URL_UI } from '../../../../config/config';
import { InfluencerStorefrontProducts } from './InfluencerStorefrontProducts';
import { InfluencerStorefrontAnalytics } from './InfluencerStorefrontAnalytics';
import { StorefrontImageUpload } from '../../../../components/storefront-image-upload/storefront-image-upload';
import { LocalService } from '../../../../core/services';
import useBodyClass from '../../../../hooks/useBodyClass';
import ZapMobileHeader from '../../../../components/mobile/ZapMobileHeader';
import zapIconActive from '../../../../assets/images/zapIconActive.svg';
import StorefrontIcon from '../../../../assets/images/storefront.svg';
import { Add, InfoOutlined } from '@mui/icons-material';
import { useIsUSInfluencer } from '../../../../hooks/useIsUSInfluencer';
import StorefrontWizard from './StorefrontWizard';
import FeatureGuideModal from '../guide/FeatureGuideModal';
import GuideButton from '../guide/GuideButton';
import { STOREFRONT_GUIDE } from '../guide/guideContent';
import { creatorDisplayName } from '../../../../modules/influencer/flowUser';

// Bordered icon tile matching the Zap Automations page header treatment.
const ZapPageIcon: React.FC = () => (
    <Box sx={{
        width: 60, height: 60, borderRadius: '10px', flexShrink: 0,
        border: '1px solid', borderColor: 'grey.200',
        display: 'flex', alignItems: 'center', justifyContent: 'center',
    }}>
        <img src={StorefrontIcon} alt="storefrontIcon Logo" width={36} height={36} />
    </Box>
);


interface Props { UserReducer: any; }

const localService = new LocalService();

const CURRENCIES = ['INR', 'USD', 'EUR', 'GBP', 'AED', 'CAD'];

// BASE_URL_UI without the http(s):// scheme — shown in the URL prefix/preview.
const BASE_HOST = (BASE_URL_UI || '').replace(/^https?:\/\//, '');

// Derive a URL-safe slug from the store name (lowercase, hyphen-separated).
const slugify = (s: string) => (s || '')
    .toLowerCase().trim()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
    .slice(0, 60);

const InfluencerStorefrontComponent: React.FC<Props> = ({ UserReducer }) => {
    useBodyClass('zap-inner-page');
    const influencer_id = UserReducer?.influencer_id;
    const account_id = UserReducer?.account_id;
    const user_id = UserReducer?.user_id;
    const isUSInfluencer = useIsUSInfluencer();

    const [loading, setLoading] = useState(true);
    const [saving, setSaving] = useState(false);
    const [slugChecking, setSlugChecking] = useState(false);
    const [slugAvailable, setSlugAvailable] = useState<boolean | null>(null);
    // True once the user has typed/regenerated their own handle — stops the
    // username default from overwriting their choice.
    const [slugTouched, setSlugTouched] = useState(false);
    const [alert, setAlert] = useState<{ type: 'success' | 'error'; msg: string } | null>(null);
    const [storefront, setStorefront] = useState<any>(null);
    const [tab, setTab] = useState(0); // 0=Products, 1=Analytics, 2=Store Settings
    const [guideOpen, setGuideOpen] = useState(false);
    const [linkCopied, setLinkCopied] = useState(false);

    const [form, setForm] = useState<any>({
        store_name: '', store_slug: '', store_logo: '', store_banner: '',
        store_description: '', website_url: '', default_currency: 'INR',
        logo_file_name: '', logo_file_type: '', logo_file_path: '', logo_storage_size: '',
        banner_file_name: '', banner_file_type: '', banner_file_path: '', banner_storage_size: '',
    });
    const [errors, setErrors] = useState<any>({
        store_name: '',
        store_slug: '',
        website_url: '',
        store_description: '',
        store_logo: '',
        store_banner: '',
        default_currency: '',
    });

    // US influencers shouldn't be offered INR — keep it only if it's already the saved value.
    const currencyOptions = isUSInfluencer
        ? CURRENCIES.filter((c) => c !== 'INR' || form.default_currency === 'INR')
        : CURRENCIES;

    // Fresh (not-yet-created) storefronts default to INR — steer US influencers to USD instead.
    useEffect(() => {
        if (!storefront && isUSInfluencer && form.default_currency === 'INR') {
            setForm((f: any) => ({ ...f, default_currency: 'USD' }));
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [isUSInfluencer, storefront]);
    const updateField = (field: string, value: any) => {
        setForm((prev: any) => ({
            ...prev,
            [field]: value,
        }));

        setErrors((prev: any) => ({
            ...prev,
            [field]: '',
        }));
    };
    const validateForm = () => {
        const e: any = {};

        if (!form.store_name.trim()) {
            e.store_name = "Store name is required.";
        }

        if (!form.store_slug.trim()) {
            e.store_slug = "Store url is required.";
        } else if (form.store_slug.trim().length < 3) {
            e.store_slug = "Username must be at least 3 characters.";
        }

        if (slugAvailable === false) {
            e.store_slug = "This username is already taken.";
        }

        if (
            form.website_url &&
            !/^https?:\/\/.+/i.test(form.website_url)
        ) {
            e.website_url = "Please enter a valid URL.";
        }

        if (!form.store_description.trim()) {
            e.store_description = "Description is required.";
        }

        if (!form.store_logo) {
            e.store_logo = "Please upload your store logo.";
        }

        if (!form.store_banner) {
            e.store_banner = "Please upload a banner image.";
        }

        if (!form.default_currency) {
            e.default_currency = "Select currency.";
        }

        setErrors(e);

        return Object.keys(e).length === 0;
    };

    useEffect(() => {
        if (!influencer_id || !account_id) { setLoading(false); return; }
        InfluencerStorefrontService.getMyStorefront(influencer_id, account_id)
            .then((res: any) => {
                if (res?.data?.rows?.length) {
                    const r = res.data.rows[0];
                    setStorefront(r);
                    setForm({
                        store_name: r.store_name || '', store_slug: r.store_slug || '',
                        store_logo: r.store_logo || '', store_banner: r.store_banner || '',
                        store_description: r.store_description || '', website_url: r.website_url || '',
                        default_currency: r.default_currency || 'INR',
                        logo_file_name: r.logo_file_name || '', logo_file_type: r.logo_file_type || '',
                        logo_file_path: r.logo_file_path || '', logo_storage_size: r.logo_storage_size || '',
                        banner_file_name: r.banner_file_name || '', banner_file_type: r.banner_file_type || '',
                        banner_file_path: r.banner_file_path || '', banner_storage_size: r.banner_storage_size || '',
                    });
                }
            })
            .catch(() => { })
            .finally(() => setLoading(false));
    }, [influencer_id, account_id]);

    const uploadPath = `accounts/${account_id}/influencer/${influencer_id}/storefront`;


    const setLogo = (m: any) => {
        setForm((f: any) => ({
            ...f,
            store_logo: m.url,
            logo_file_name: m.file_name,
            logo_file_type: m.file_type,
            logo_file_path: m.file_path,
            logo_storage_size: m.storage_size,
        }));

        setErrors((prev: any) => ({
            ...prev,
            store_logo: "",
        }));
    };
    const removeLogo = () => setForm((f: any) => ({ ...f, store_logo: '', logo_file_name: '', logo_file_type: '', logo_file_path: '', logo_storage_size: '' }));
    const setBanner = (m: any) => {
        setForm((f: any) => ({
            ...f,
            store_banner: m.url,
            banner_file_name: m.file_name,
            banner_file_type: m.file_type,
            banner_file_path: m.file_path,
            banner_storage_size: m.storage_size,
        }));

        setErrors((prev: any) => ({
            ...prev,
            store_banner: "",
        }));
    };
    const removeBanner = () => setForm((f: any) => ({ ...f, store_banner: '', banner_file_name: '', banner_file_type: '', banner_file_path: '', banner_storage_size: '' }));

    // Check availability of a given slug.
    const checkSlug = (slug: string) => {
        setSlugAvailable(null);
        if (!slug || slug.length < 3) return;
        setSlugChecking(true);
        InfluencerStorefrontService.checkSlug(slug, influencer_id, account_id)
            .then((res: any) => setSlugAvailable(res?.data?.available ?? null))
            .catch(() => setSlugAvailable(null))
            .finally(() => setSlugChecking(false));
    };

    // First-time setup: pre-fill the handle with the influencer's own username so
    // it's ready to use as-is. They can freely edit it afterwards (slugTouched),
    // and for an existing store we never auto-change the saved slug (it would
    // break the already-shared /shop link).
    useEffect(() => {
        if (storefront || slugTouched) return;
        const base = slugify(creatorDisplayName(UserReducer) || '')
            || slugify(form.store_name);
        if (!base) return;
        setForm((f: any) => (f.store_slug === base ? f : { ...f, store_slug: base }));
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [UserReducer, form.store_name, storefront, slugTouched]);

    // Check availability whenever the handle changes (debounced). Skip re-checking
    // an existing store's own saved slug.
    useEffect(() => {
        const s = form.store_slug;
        if (storefront && s === storefront.store_slug) { setSlugAvailable(null); return; }
        if (!s || s.length < 3) { setSlugAvailable(null); return; }
        const t = setTimeout(() => checkSlug(s), 400);
        return () => clearTimeout(t);
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [form.store_slug, storefront]);

    // Auto-generate a fresh, likely-unique handle from the username/name (+ random suffix).
    const regenerateSlug = () => {
        const base = slugify(creatorDisplayName(UserReducer) || form.store_name) || 'store';
        const candidate = `${base}-${Math.random().toString(36).slice(2, 6)}`;
        setSlugTouched(true);
        setForm((f: any) => ({ ...f, store_slug: candidate }));
    };

    const handleSubmit = async () => {
        if (!validateForm()) {
            setAlert({
                type: "error",
                msg: "Please complete all required fields before creating your storefront."
            });

            return;
        }
        // if (!form.store_name || !form.store_slug) {
        //     setAlert({ type: 'error', msg: 'Store name and slug are required' });
        //     return;
        // }
        setSaving(true);
        setAlert(null);
        try {
            const res = await InfluencerStorefrontService.setupStorefront({ ...form, influencer_id, account_id, user_id });
            if (res?.status === 1 && res?.data?.rows?.length) {
                const successMessage = storefront
                    ? 'Storefront updated successfully!'
                    : 'Your storefront is now live!';
                setStorefront(res.data.rows[0]);
                // setAlert({ type: 'success', msg: storefront ? 'Storefront updated!' : 'Your storefront is live!' });
                setAlert({
                    type: 'success',
                    msg: successMessage,
                });

                localService.toastify(successMessage, 'success', 3000);
            } else {
                const errorMessage = res?.info || 'Failed to save storefront';

                setAlert({
                    type: 'error',
                    msg: errorMessage,
                });

                localService.toastify(errorMessage, 'error', 3000);
            }
        } catch {
            setAlert({
                type: 'error',
                msg: 'Something went wrong',
            });

            localService.toastify(
                'Something went wrong',
                'error',
                3000
            );
        } finally {
            setSaving(false);
        }
    };

    const publicUrl = storefront ? `${BASE_URL_UI}/shop/${storefront.store_slug}` : '';
    // Scheme-stripped version for display only (copy/open still use the full URL).
    const publicUrlDisplay = publicUrl.replace(/^https?:\/\//, '');

    const copyLink = async () => {
        try {
            await navigator.clipboard.writeText(publicUrl);
            setLinkCopied(true);
            setTimeout(() => setLinkCopied(false), 2000);
        } catch { }
    };

    if (loading) return <Box display="flex" justifyContent="center" mt={6}><CircularProgress /></Box>;

    const settingsForm = (
        <>
            <Box
                sx={{
                    maxWidth: 1400,
                    mx: 'auto'
                }}
            >
                <Grid container spacing={4}>

                    {/* LEFT SIDE */}

                    <Grid item xs={12} md={8}>
                        {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}
                        {/* Store Information */}

                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 3,
                                    lg: 4,
                                },
                                borderRadius: 4,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                mb: 4
                            }}
                        >

<Box mb={4}>
    <Stack
        direction="row"
        spacing={1}
        alignItems="center"
        mb={0.5}
    >
        <Typography
            variant="h5"
            fontWeight={700}
            sx={{ fontSize: { xs: 18, md: 20 } }}
        >
            Store Information
        </Typography>

        <Tooltip title="Basic information about your storefront.">           
            <InfoOutlined
                sx={{
                    fontSize: 18,
                    color: "text.secondary",
                    cursor: "pointer",
                }}
            />
        </Tooltip>
    </Stack>

    {/* <Typography
        color="text.secondary"
        sx={{ fontSize: { xs: 14, md: 16 } }}
    >
        Basic information about your storefront.
    </Typography> */}
</Box>

                            <Grid container spacing={3}>

                                <Grid item xs={12} sm={6}>
                                    <Stack spacing={1}>
    <Stack
        direction="row"
        spacing={0.5}
        alignItems="center"
    >
        <Typography
            variant="body2"
            sx={{ fontSize: { xs: 12, md: 12 } }}
            fontWeight={500}
        >
            Store Name *
        </Typography>

        <Tooltip title="This name will be displayed publicly on your storefront.">
            <InfoOutlined
                sx={{
                    fontSize: 16,
                    color: "text.secondary",
                    cursor: "pointer",
                }}
            />
        </Tooltip>
    </Stack>

    <TextField
        fullWidth
        size="small"
        placeholder="Enter store name"
        value={form.store_name}
        onChange={(e) => updateField("store_name", e.target.value)}
        error={!!errors.store_name}
        helperText={errors.store_name}
    />
</Stack>
                                    {/* <TextField
                                        fullWidth
                                        label="Store Name *"
                                        value={form.store_name}
                                        onChange={(e) => updateField("store_name", e.target.value)}
                                        error={!!errors.store_name}
                                        helperText={errors.store_name}
                                        className='input-style input-common'
                                        size="small"

                                    /> */}
                                </Grid>
                                <Grid item xs={12} sm={6}>
    <Stack spacing={1}>
        <Stack direction="row" spacing={0.5} alignItems="center">
            <Typography variant="body2" sx={{ fontSize: { xs: 12, md: 12 } }}
            fontWeight={500}>
                Store url *
            </Typography>

            <Tooltip title="This is the unique username at the end of your storefront's web address. Enter your own or auto-generate one — it must be available.">
                <InfoOutlined
                    sx={{
                        fontSize: 16,
                        color: "text.secondary",
                        cursor: "pointer",
                    }}
                />
            </Tooltip>
        </Stack>

        <TextField
            fullWidth
            size="small"
            placeholder="your-username"
            value={form.store_slug}
            onChange={(e) => {
                setSlugTouched(true);
                updateField("store_slug", slugify(e.target.value));
            }}
            InputProps={{
                startAdornment: (
                   <InputAdornment
        position="start"
        sx={{
            mr: 0.25, // reduce the gap
            "& .MuiTypography-root": {
                color: "text.secondary",
                fontWeight: 500,
            },
        }}
    >
                        {BASE_HOST}/shop/
                    </InputAdornment>
                ),
                endAdornment: (
                    <InputAdornment position="start">
                        <Tooltip title="Auto-generate a username">
                            <IconButton
                                edge="end"
                                size="small"
                                onClick={regenerateSlug}
                            >
                                <AutorenewIcon fontSize="small" />
                            </IconButton>
                        </Tooltip>
                    </InputAdornment>
                ),
            }}
            error={!!errors.store_slug || slugAvailable === false}
            helperText={
                errors.store_slug ||
                (
                    slugChecking
                        ? "Checking..."
                        : slugAvailable === true
                            ? "✓ Available"
                            : slugAvailable === false
                                ? "Already taken — try another or auto-generate"
                                : "Use your own username or auto-generate one"
                )
            }
        />
    </Stack>
</Grid>
                                <Grid item xs={12} sm={12}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" mb={1}>
    <Typography variant="body2" sx={{ fontSize: { xs: 12, md: 12 } }}
            fontWeight={500}>
        Website / Social URL (Optional)
    </Typography>

    <Tooltip title="Add your website or social media profile link.">
        <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
    </Tooltip>
</Stack>
<TextField className='input-style input-common'
                                        size="small" fullWidth value={form.website_url}
                                        onChange={(e) =>
                                            updateField("website_url", e.target.value)
                                        }

                                        error={!!errors.website_url}

                                        helperText={errors.website_url}
                                        placeholder="https://instagram.com/yourhandle" />
                                    {/* <TextField fullWidth label="Store Slug *" value={form.store_slug}
                                        sx={{ '& .MuiInputBase-root': { bgcolor: '#F5F5F5' }, '& .MuiInputBase-input': { color: 'rgba(0,0,0,0.55)' } }}
                                        InputProps={{
                                            readOnly: true,
                                            startAdornment: <InputAdornment position="start">/shop/</InputAdornment>,
                                            endAdornment: (
                                                <InputAdornment position="end">
                                                    <Tooltip title="Generate a different slug">
                                                        <IconButton edge="end" size="small" onClick={regenerateSlug}>
                                                            <AutorenewIcon fontSize="small" />
                                                        </IconButton>
                                                    </Tooltip>
                                                </InputAdornment>
                                            ),
                                        }}
                                        // helperText={
                                        //     slugChecking ? 'Checking availability…' :
                                        //     slugAvailable === true ? '✓ Available' :
                                        //     slugAvailable === false ? '✗ Already taken — click ↻ to generate a new one' :
                                        //     'Auto-generated from your store name'
                                        // }
                                        // error={slugAvailable === false} 
                                        error={
                                            !!errors.store_slug ||
                                            slugAvailable === false
                                        }

                                        helperText={
                                            errors.store_slug ||
                                            (
                                                slugChecking
                                                    ? "Checking..."
                                                    : slugAvailable === true
                                                        ? "✓ Available"
                                                        : slugAvailable === false
                                                            ? "Already taken"
                                                            : "Auto generated"
                                            )
                                        }
                                    /> */}
                                </Grid>

                                
                                {/* <Grid item xs={12}>
                                    <TextField className='input-style input-common'
                                        size="small" fullWidth value={form.website_url}
                                        onChange={(e) =>
                                            updateField("website_url", e.target.value)
                                        }

                                        error={!!errors.website_url}

                                        helperText={errors.website_url}
                                        placeholder="https://instagram.com/yourhandle" />
                                </Grid> */}
                                <Grid item xs={12}>
                                    <Stack direction="row" spacing={0.5} alignItems="center" mb={1}>
    <Typography variant="body2" sx={{ fontSize: { xs: 12, md: 12 } }}
            fontWeight={500}>
        Store Description
    </Typography>

    <Tooltip title="Describe your storefront. This will be shown publicly.">
        <InfoOutlined sx={{ fontSize: 16, color: "text.secondary" }} />
    </Tooltip>
</Stack>
                                    <TextField fullWidth multiline rows={3}
                                        value={form.store_description}
                                        onChange={(e) =>
                                            updateField("store_description", e.target.value)
                                        }

                                        error={!!errors.store_description}

                                        helperText={
                                            errors.store_description ||
                                            `${form.store_description.length}/250`
                                        }
                                    />
                                </Grid>

                            </Grid>

                        </Paper>

                        {/* Branding Card */}

                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 3,
                                    lg: 4,
                                },
                                borderRadius: 4,
                                border: '1px solid',
                                borderColor: 'grey.200'
                            }}
                        >

                         <Stack
    direction="row"
    spacing={1}
    alignItems="center"
    mb={2}
>
    <Typography
        variant="h5"
        fontWeight={700}
        sx={{ fontSize: { xs: 18, md: 20 }, display:'block' }}
    >
        Branding
    </Typography>

    <Tooltip title="Upload your logo and banner to personalize your storefront.">
        <InfoOutlined
            sx={{
                fontSize: 18,
                color: "text.secondary",
                cursor: "pointer",
            }}
        />
    </Tooltip>
</Stack>

                        

                            <Grid container spacing={4}>

                                {/* Logo */}

                                {/* Banner */}
                                <Grid item xs={12} sm={12}>
                                    <StorefrontImageUpload error={!!errors.store_logo} helperText={errors.store_logo} label="Logo" value={form.store_logo} uploadPath={uploadPath}
                                        previewVariant="logo" hint="Square image, PNG/JPG, max 2MB"
                                        onChange={setLogo} onRemove={removeLogo}

                                    />

                                    {/* {errors.store_logo && (
<Typography
variant="caption"
color="error"
>
{errors.store_logo}
</Typography>
)} */}
                                </Grid>
                                <Grid item xs={12} sm={12}>
                                    <StorefrontImageUpload label="Banner" error={!!errors.store_banner} value={form.store_banner} helperText={errors.store_banner} uploadPath={uploadPath}
                                        previewVariant="banner" hint="Wide image, PNG/JPG, max 2MB"
                                        onChange={setBanner} onRemove={removeBanner} />
                                    {/* {errors.store_banner && (
<Typography
variant="caption"
color="error"
>
{errors.store_banner}
</Typography>
)} */}
                                </Grid>
                                <Grid item xs={12} sm={4}>
                                    <FormControl fullWidth>
                                        <InputLabel>Default Currency</InputLabel>
                                        <Select value={form.default_currency} label="Default Currency"
                                            onChange={e => setForm((f: any) => ({ ...f, default_currency: e.target.value }))} error={!!errors.default_currency}>
                                            {currencyOptions.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                                        </Select>
                                        <FormHelperText>
                                            {errors.default_currency}
                                        </FormHelperText>

                                    </FormControl>

                                </Grid>
                                <Grid item xs={12}>
                                    {/* <Button
fullWidth
size="large"
sx={{
height:56,
borderRadius:3,
fontWeight:700,
fontSize:16,
textTransform:"none",
color:"#fff",
background:
'linear-gradient(135deg,#2563EB,#7C3AED)',

"&:hover":{
transform:"translateY(-2px)"
}
}}
>
🚀 Launch Storefront
</Button> */}
                                    <Button fullWidth disabled={saving || slugAvailable === false} variant="contained" size="large" onClick={handleSubmit}
                                        className="btn btn-primary"
                                        sx={{ mt: 4 }}
                                    >
                                        {saving ? <CircularProgress size={20} color="inherit" /> : (storefront ? 'Update Storefront' : 'Create Storefront')}
                                    </Button>
                                </Grid>

                            </Grid>

                        </Paper>

                    </Grid>

                    {/* RIGHT SIDE */}

                    <Grid item xs={12} lg={4} sx={{ minHeight: 420 }}>

                        <Paper
                            elevation={0}
                            sx={{
                                p: {
                                    xs: 2,
                                    md: 3,
                                    lg: 4,
                                },
                                borderRadius: 4,
                                border: '1px solid',
                                borderColor: 'grey.200',
                                position: 'sticky',
                                top: 100
                            }}
                        >

                            <Chip
                                label="Live Preview"
                                color="primary"
                                sx={{ mb: 3 }}
                            />

                            <Box
                                sx={{
                                    borderRadius: 3,
                                    overflow: 'hidden',
                                    border: '1px solid',
                                    borderColor: 'grey.200'
                                }}
                            >

                                <Box
                                    sx={{
                                        height: 120,
                                        bgcolor: '#EEF2FF',
                                        backgroundImage: form.store_banner
                                            ? `url(${form.store_banner})`
                                            : undefined,
                                        backgroundSize: 'cover',
                                        backgroundPosition: 'center'
                                    }}
                                />

                                <Box
                                    sx={{
                                        p: 3,
                                        mt: -5
                                    }}
                                >

                                    <Box
                                        sx={{
                                            width: 80,
                                            height: 80,
                                            borderRadius: '50%',
                                            bgcolor: '#fff',
                                            
                                            overflow: 'hidden',
                                            mb: 2,
                                            padding:'8px',
                                            border:'1px solid #e5e7eb'
                                        }}
                                    >
                                        {form.store_logo && (
                                            <img
                                                src={form.store_logo}
                                                alt=""
                                                style={{
                                                    width: '100%',
                                                    height: '100%',
                                                    objectFit: 'cover'
                                                }}
                                            />
                                        )}
                                    </Box>

                                    <Typography
                                        variant="h6"
                                        fontWeight={700}
                                        sx={{ fontSize: { xs: 14, md: 16 } }}
                                    >
                                        {form.store_name || 'Your Store'}
                                    </Typography>

                                    <Typography
                                        color="text.secondary"
                                        sx={{ mt: 1, fontSize: { xs: 14, md: 16 } }}
                                    >
                                        {form.store_description ||
                                            'Your description will appear here.'}
                                    </Typography>

                                    <Typography
                                        component="a"
                                        href={`https://${BASE_HOST}/shop/${form.store_slug || 'store-name'}`}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        sx={{
                                            mt: 3,
                                            color: 'primary.main',
                                            fontWeight: 600,
                                            textDecoration: 'none',
                                            cursor: 'pointer',
                                            '&:hover': {
                                                textDecoration: 'underline'
                                            }
                                        }}
                                    >
                                        {BASE_HOST}/shop/{form.store_slug || 'store-name'}
                                    </Typography>

                                </Box>

                            </Box>

                        </Paper>

                    </Grid>

                </Grid>
            </Box>
            {/* <Box maxWidth={820} pt={1}>
            {alert && <Alert severity={alert.type} sx={{ mb: 2 }} onClose={() => setAlert(null)}>{alert.msg}</Alert>}
            <Paper sx={{ p: 3 }} variant="outlined">
                <Grid container spacing={3}>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Store Name *" value={form.store_name}
                            onChange={e => setForm((f: any) => ({ ...f, store_name: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={6}>
                        <TextField fullWidth label="Store Slug *" value={form.store_slug}
                            sx={{ '& .MuiInputBase-root': { bgcolor: '#F5F5F5' }, '& .MuiInputBase-input': { color: 'rgba(0,0,0,0.55)' } }}
                            InputProps={{
                                readOnly: true,
                                startAdornment: <InputAdornment position="start">/shop/</InputAdornment>,
                                endAdornment: (
                                    <InputAdornment position="end">
                                        <Tooltip title="Generate a different slug">
                                            <IconButton edge="end" size="small" onClick={regenerateSlug}>
                                                <AutorenewIcon fontSize="small" />
                                            </IconButton>
                                        </Tooltip>
                                    </InputAdornment>
                                ),
                            }}
                            helperText={
                                slugChecking ? 'Checking availability…' :
                                slugAvailable === true ? '✓ Available' :
                                slugAvailable === false ? '✗ Already taken — click ↻ to generate a new one' :
                                'Auto-generated from your store name'
                            }
                            error={slugAvailable === false} />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth label="Website / Social URL (optional)" value={form.website_url}
                            onChange={e => setForm((f: any) => ({ ...f, website_url: e.target.value }))}
                            placeholder="https://instagram.com/yourhandle" />
                    </Grid>
                    <Grid item xs={12}>
                        <TextField fullWidth multiline rows={3} label="Store Description"
                            value={form.store_description}
                            onChange={e => setForm((f: any) => ({ ...f, store_description: e.target.value }))} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <StorefrontImageUpload label="Logo" value={form.store_logo} uploadPath={uploadPath}
                            previewVariant="logo" hint="Square image, PNG/JPG, max 2MB"
                            onChange={setLogo} onRemove={removeLogo} />
                    </Grid>
                    <Grid item xs={12} sm={12}>
                        <StorefrontImageUpload label="Banner" value={form.store_banner} uploadPath={uploadPath}
                            previewVariant="banner" hint="Wide image, PNG/JPG, max 2MB"
                            onChange={setBanner} onRemove={removeBanner} />
                    </Grid>
                    <Grid item xs={12} sm={4}>
                        <FormControl fullWidth>
                            <InputLabel>Default Currency</InputLabel>
                            <Select value={form.default_currency} label="Default Currency"
                                onChange={e => setForm((f: any) => ({ ...f, default_currency: e.target.value }))}>
                                {CURRENCIES.map(c => <MenuItem key={c} value={c}>{c}</MenuItem>)}
                            </Select>
                        </FormControl>
                    </Grid>
                    <Grid item xs={12}>
                        <Button variant="contained" size="large" onClick={handleSubmit}
                            disabled={saving || slugAvailable === false}>
                            {saving ? <CircularProgress size={20} color="inherit" /> : (storefront ? 'Update Storefront' : 'Create Storefront')}
                        </Button>
                    </Grid>
                </Grid>
            </Paper>
        </Box> */}
        </>
    );

    // First-time: no storefront yet → setup screen.
    if (!storefront) {
        return (
            // <Box p={3}>
            //     <Box display="flex" alignItems="center" gap={1.5} mb={1}>
            //         <StorefrontIcon color="primary" />
            //         <Typography variant="h5" fontWeight={700}>Create your Storefront</Typography>
            //     </Box>
            //     <Typography variant="body2" color="text.secondary" mb={3} maxWidth={680}>
            //         Build a shareable shop of products with your own affiliate links. Add it to your bio,
            //         share it anywhere, and track how many people view your store and click through to buy.
            //     </Typography>
            //     {settingsForm}
            // </Box>
            <Box
                sx={{
                    p: {
                        xs: 2,
                        md: 3,
                        lg: 4,
                    },
                }}
            >
                <Box
                    sx={{
                        display: 'flex',
                        alignItems: 'center',
                        gap: { xs: 1.5, lg: 2 },
                        mb: 4,
                        // OLD: maxWidth: 1400 — the title and "How to use" sat far
                        // outside the 760px wizard below. Same width, same centre.
                        maxWidth: 760,
                        mx: 'auto',
                    }}
                >
                    <ZapPageIcon />
                    <Box>
                        <Typography sx={{ fontSize: { xs: 20, lg: 24 }, fontWeight: 700, color: '#111' }}>
                            Launch Your Storefront
                        </Typography>
                        {/* OLD: a four-line pitch above an already-long form.
                            Create your own branded shop, share a single link in your bio,
                            recommend products you love, and earn commissions from every purchase. */}
                        <Typography sx={{ color: 'text.secondary', fontSize: { xs: 14, lg: 16 }, mt: 0.5, maxWidth: 650 }}>
                            Your own shop, in about a minute. We only need a name to get started.
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                        <GuideButton feature="Storefront" onClick={() => setGuideOpen(true)} />
                    </Box>
                </Box>
                <FeatureGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} guide={STOREFRONT_GUIDE} />

                {/* OLD: the whole settings form on first visit — name, url, description,
                    logo AND banner all at once, every one of them required client-side
                    even though the API only needs a name and a slug. Now a 4-step
                    wizard; the form below still powers Store Settings.
                    {settingsForm} */}
                <StorefrontWizard
                    influencerId={influencer_id}
                    accountId={account_id}
                    userId={user_id}
                    displayName={creatorDisplayName(UserReducer)}
                    isUS={isUSInfluencer}
                    onCreated={(row: any) => {
                        setStorefront(row);
                        setForm({
                            store_name: row.store_name || '', store_slug: row.store_slug || '',
                            store_logo: row.store_logo || '', store_banner: row.store_banner || '',
                            store_description: row.store_description || '', website_url: row.website_url || '',
                            default_currency: row.default_currency || 'INR',
                            logo_file_name: row.logo_file_name || '', logo_file_type: row.logo_file_type || '',
                            logo_file_path: row.logo_file_path || '', logo_storage_size: row.logo_storage_size || '',
                            banner_file_name: row.banner_file_name || '', banner_file_type: row.banner_file_type || '',
                            banner_file_path: row.banner_file_path || '', banner_storage_size: row.banner_storage_size || '',
                        });
                    }}
                />
            </Box>
        );
    }

    // Existing storefront → live link banner + tabs.
    return (
        <Box sx={{ maxWidth: 1400, mx: 'auto' }}>
            <ZapMobileHeader title="Zap Storefront" />
            <Box sx={{ px: 3, pt: 3, pb: 1.5 }}>
                <Box display="flex" alignItems="center" gap={{ xs: 1.5, lg: 2 }} flexWrap="wrap" mb={2}>
                    <ZapPageIcon />
                    <Box>
                        <Box display="flex" alignItems="center" gap={1}>
                            <Typography sx={{ fontSize: { xs: 18, md: 22 }, fontWeight: 700, color: '#111' }}>{storefront.store_name}</Typography>
                            <Chip size="small" color="success" label="Live" />
                        </Box>
                        <Typography sx={{ color: 'text.secondary', fontSize: { xs: 13, md: 15 }, mt: 0.5 }}>
                            Your public storefront is live and ready to share.
                        </Typography>
                    </Box>
                    <Box sx={{ ml: 'auto' }}>
                        <GuideButton feature="Storefront" onClick={() => setGuideOpen(true)} />
                    </Box>
                </Box>
                <FeatureGuideModal open={guideOpen} onClose={() => setGuideOpen(false)} guide={STOREFRONT_GUIDE} />
<Box
    sx={{
        display: "flex",
        justifyContent: "space-between",
        alignItems: "center",
        mb: 1,
        maxWidth: 700,
    }}
>
    <Typography
        variant="subtitle2"
        fontWeight={600}
    >
        Store URL
    </Typography>

    <Link
        component="button"
        underline="hover"
        onClick={() => window.open(publicUrl, "_blank")}
        sx={{
            fontWeight: 600,
            fontSize: 14,
        }}
    >
        View Store
    </Link>
</Box>

<Paper
    variant="outlined"
    sx={{
        p: 1.5,
        borderRadius: "10px",
        display: "flex",
        alignItems: "center",
        gap: 1,
        maxWidth: 700,
        bgcolor: "#FAFAFA",
    }}
>
    <Typography
        variant="body2"
        sx={{
            flexGrow: 1,
            fontFamily: "monospace",
            overflow: "hidden",
            textOverflow: "ellipsis",
            whiteSpace: "nowrap",
        }}
    >
        {publicUrlDisplay}
    </Typography>

    <Tooltip title={linkCopied ? "Copied!" : "Copy link"}>
        <IconButton
            size="small"
            onClick={copyLink}
            color={linkCopied ? "success" : "default"}
        >
            <ContentCopyIcon fontSize="small" />
        </IconButton>
    </Tooltip>
</Paper>
            </Box>

            <Box sx={{ py: 2, px: 3, maxWidth: 1400, mx: 'auto' }}>
                <Tabs
                    value={tab}
                    onChange={(_, v) => { setTab(v); setAlert(null); }}
                    variant="fullWidth"
                    sx={{
                        minHeight: 26,
                        bgcolor: '#ececf0',
                        borderRadius: '999px',
                        p: '6px',
                        width: '100%',
                        maxWidth: 448,
                        '& .MuiTabs-indicator': { display: 'none' },
                    }}
                >
                    {['Products', 'Analytics', 'Store Settings'].map(label => (
                        <Tab
                            key={label}
                            label={label}
                            sx={{
                                minHeight: 20,
                                textTransform: 'none',
                                fontSize: 12,
                                fontWeight: 500,
                                borderRadius: '999px',
                                color: '#000',
                                lineHeight: .85,
                                px: 1.5,
                                minWidth: 0,
                                '&:hover': {
                                    bgcolor: '#000',
                                    color: '#fff',
                                },
                                '&.Mui-selected': {
                                    bgcolor: '#000',
                                    color: '#fff',
                                    fontWeight: 600,
                                },
                            }}
                        />
                    ))}
                </Tabs>
            </Box>

            {tab === 0 && (
                <Box p={3}>
                    <InfluencerStorefrontProducts storefront={storefront} />
                </Box>
            )}
            {tab === 1 && (
                <Box p={3}>
                    <InfluencerStorefrontAnalytics influencer_id={influencer_id} account_id={account_id} />
                </Box>
            )}
            {tab === 2 && (
                <Box p={3}>
                    {settingsForm}
                </Box>
            )}
        </Box>
    );
};

const mapStateToProps = (state: any) => ({ UserReducer: state.UserReducer.UserData });
export const InfluencerStorefront = connect(mapStateToProps)(InfluencerStorefrontComponent);
